import React, { useState, useEffect, Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Box } from '@mui/material';

import { useAuth } from './context/AuthContext';
import { useBrand } from './context/BrandContext';

// Layout Components
import Layout from './components/Layout/Layout';
import AuthLayout from './components/Layout/AuthLayout';
import ErrorBoundary from './components/Common/ErrorBoundary';
import LoadingScreen from './components/Common/LoadingScreen';

// Auth Pages - Load immediately (small, critical)
import Login from './pages/Auth/Login';
import Register from './pages/Auth/Register';
import ForgotPassword from './pages/Auth/ForgotPassword';
import ResetPassword from './pages/Auth/ResetPassword';
import VerifyEmail from './pages/Auth/VerifyEmail';
import OAuthCallback from './pages/Auth/OAuthCallback';
import { TermsOfService, PrivacyPolicy, DataDeletion, LEGAL_PATHS } from './pages/Legal/LegalPages';
import Home from './pages/Public/Home';

// Main Pages - Lazy load for better performance
const Dashboard = lazy(() => import('./pages/Dashboard/Dashboard'));
const Brands = lazy(() => import('./pages/Brands/Brands'));
const BrandDetail = lazy(() => import('./pages/Brands/BrandDetail'));
const SocialAccounts = lazy(() => import('./pages/SocialAccounts/SocialAccounts'));
const Posts = lazy(() => import('./pages/Posts/Posts'));
const Calendar = lazy(() => import('./pages/Posts/Calendar'));
const Composer = lazy(() => import('./pages/Composer/Composer'));
const Analytics = lazy(() => import('./pages/Analytics/Analytics'));
const MediaLibrary = lazy(() => import('./pages/Media/MediaLibrary'));
const Reports = lazy(() => import('./pages/Reports/Reports'));
const AIAssistant = lazy(() => import('./pages/AI/AIAssistant'));
const Settings = lazy(() => import('./pages/Settings/Settings'));
const Profile = lazy(() => import('./pages/Profile/Profile'));
const Integrations = lazy(() => import('./pages/Integrations/Integrations'));
const Inbox = lazy(() => import('./pages/Inbox/Inbox'));
const BlogComposer = lazy(() => import('./pages/Blog/BlogComposer'));
const Invitations = lazy(() => import('./pages/Team/Invitations'));

// Admin Pages - Lazy load
const AdminDashboard = lazy(() => import('./pages/Admin/AdminDashboard'));
const AdminUsers = lazy(() => import('./pages/Admin/AdminUsers'));
const AdminBrands = lazy(() => import('./pages/Admin/AdminBrands'));

function App() {
  const { user, loading: authLoading } = useAuth();
  const { selectedBrand, loading: brandLoading } = useBrand();
  const { pathname } = useLocation();

  // Add a safety timeout - if loading takes too long, show login page anyway
  const [forceShow, setForceShow] = useState(false);
  
  useEffect(() => {
    if (!authLoading) {
      setForceShow(false);
      return;
    }
    
    const timer = setTimeout(() => {
      if (authLoading) {
        console.warn('Auth loading timeout - forcing login page display');
        setForceShow(true);
      }
    }, 3000); // 3 second timeout
    
    return () => clearTimeout(timer);
  }, [authLoading]);

  // Legal pages are public for everyone — logged in or not, and without
  // waiting on auth — since platform reviewers open these URLs directly.
  if (LEGAL_PATHS.includes(pathname)) {
    return (
      <Routes>
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/data-deletion" element={<DataDeletion />} />
      </Routes>
    );
  }

  if (authLoading && !forceShow) {
    return <LoadingScreen message="Loading..." />;
  }

  // Public routes (no authentication required)
  if (!user) {
    // The marketing landing page has its own top bar and footer, so it sits
    // outside AuthLayout's two-panel sign-in frame.
    if (pathname === '/') {
      return <Home />;
    }
    return (
      <AuthLayout>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/auth/:platform/callback" element={<OAuthCallback />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </AuthLayout>
    );
  }

  // Protected routes (authentication required)
  return (
    <ErrorBoundary>
      <Layout>
        <Box sx={{ flexGrow: 1, minHeight: '100vh' }}>
          <Suspense fallback={<LoadingScreen message="Loading page..." />}>
            <Routes>
              {/* OAuth callback (Google/Facebook/etc. redirect back here while
                  already logged in — must be reachable from this authenticated
                  route tree too, not just the logged-out one above, otherwise
                  it falls through to the "*" redirect below and the auth code
                  Google sent is lost before OAuthCallback ever mounts) */}
              <Route path="/auth/:platform/callback" element={<OAuthCallback />} />

              {/* Dashboard */}
              <Route path="/" element={<Dashboard />} />
              <Route path="/dashboard" element={<Dashboard />} />

              {/* Brands */}
              <Route path="/brands" element={<Brands />} />
              <Route path="/brands/:brandId" element={<BrandDetail />} />

              {/* Social Accounts */}
              <Route path="/brands/:brandId/social-accounts" element={<SocialAccounts />} />
              <Route path="/brands/:brandId/integrations" element={<Integrations />} />

              {/* Posts */}
              <Route path="/brands/:brandId/posts" element={<Posts />} />
              <Route path="/brands/:brandId/calendar" element={<Calendar />} />
              <Route path="/brands/:brandId/inbox" element={<Inbox />} />
              <Route path="/brands/:brandId/blog" element={<BlogComposer />} />
              <Route path="/brands/:brandId/composer" element={<Composer />} />
              <Route path="/composer" element={<Composer />} />

              {/* Analytics */}
              <Route path="/brands/:brandId/analytics" element={<Analytics />} />

              {/* Media */}
              <Route path="/brands/:brandId/media" element={<MediaLibrary />} />

              {/* Reports */}
              <Route path="/brands/:brandId/reports" element={<Reports />} />

              {/* AI Assistant */}
              <Route path="/brands/:brandId/ai" element={<AIAssistant />} />

              {/* Settings */}
              <Route path="/settings" element={<Settings />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/invitations" element={<Invitations />} />

              {/* Admin Routes */}
              {user.role === 'admin' && (
                <>
                  <Route path="/admin" element={<AdminDashboard />} />
                  <Route path="/admin/users" element={<AdminUsers />} />
                  <Route path="/admin/brands" element={<AdminBrands />} />
                </>
              )}

              {/* Redirect to dashboard if no brand selected */}
              {!selectedBrand && !brandLoading && (
                <Route path="*" element={<Navigate to="/brands" replace />} />
              )}

              {/* Default redirect */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </Box>
      </Layout>
    </ErrorBoundary>
  );
}

export default App;







